import { format, parseISO } from 'date-fns';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { CHART_AXIS_TEXT, CHART_GRID, chartSeries } from '@/shared/lib/chart-colors';
import { formatNumber } from '@/shared/lib/format';

import type { DashboardTrendPoint } from '../types/dashboard.types';

/**
 * Orders over time.
 *
 * Single series, so there is no legend — the title names what is plotted, and
 * a legend box for one thing is pure furniture.
 *
 * Revenue is never plotted here: it is per currency, and summing currencies
 * would be meaningless. Orders and money are also different units, so they
 * would need two y-axes,
 * which makes the crossing point of the two lines meaningless and is the most
 * common way a dashboard chart lies.
 */
export function OrdersTrendChart({
  data,
  isLoading,
  error,
  onRetry,
  metric = 'orders',
}: {
  data: DashboardTrendPoint[] | undefined;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  metric?: 'orders' | 'completed';
}) {
  const isCompleted = metric === 'completed';
  const color = chartSeries(isCompleted ? 1 : 0);
  const gradientId = `trend-${metric}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">
          {isCompleted ? 'Completed orders over time' : 'Orders over time'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-56 w-full" />
        ) : error ? (
          <ErrorState error={error} onRetry={onRetry} className="h-56" />
        ) : !data || data.length === 0 ? (
          <EmptyState
            title="No data for this period"
            description="No orders were recorded in the selected date range."
            className="h-56"
          />
        ) : (
          <ResponsiveContainer width="100%" height={224}>
            <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                </linearGradient>
              </defs>

              {/* Horizontal rules only: vertical grid lines add noise without
                  helping anyone read a value off a time axis. */}
              <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />

              <XAxis
                dataKey="date"
                tickFormatter={(value: string) => format(parseISO(value), 'd MMM')}
                stroke={CHART_AXIS_TEXT}
                fontSize={11}
                tickLine={false}
                axisLine={false}
                minTickGap={24}
              />
              <YAxis
                stroke={CHART_AXIS_TEXT}
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={52}
                tickFormatter={(value: number) => formatNumber(value)}
              />

              <Tooltip
                cursor={{ stroke: CHART_GRID }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const point = payload[0];
                  return (
                    <div className="bg-popover text-popover-foreground rounded-md border px-2.5 py-1.5 text-xs shadow-md">
                      <p className="text-muted-foreground mb-1 text-[11px]">
                        {format(parseISO(String(label)), 'd MMM yyyy')}
                      </p>
                      <p className="flex items-center gap-1.5 font-medium tabular-nums">
                        <span
                          className="size-2 rounded-full"
                          style={{ backgroundColor: color }}
                          aria-hidden
                        />
                        {formatNumber(Number(point?.value))}{' '}
                        <span className="text-muted-foreground font-normal">
                          {isCompleted ? 'completed' : 'orders'}
                        </span>
                      </p>
                    </div>
                  );
                }}
              />

              <Area
                type="monotone"
                dataKey={metric}
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                // A dot per day turns a 90-day range into a smear; show one
                // only on hover.
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
